import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Factory, Cog, TrendingUp, DollarSign, Clock, Shield, BarChart3, Hammer } from 'lucide-react';

const SBAMadeInAmerica = () => {
  return (
    <div className="bg-[#FAFAFA]">
      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.pexels.com/photos/1108101/pexels-photo-1108101.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#1a1a2e]/95 via-[#16213e]/85 to-[#0f3460]/70" />
        </div>
        <div className="absolute inset-0 noise-overlay" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-8 w-full py-28">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-red-500/15 backdrop-blur-sm border border-red-400/20 rounded-full px-4 py-1.5 mb-8">
              <Shield className="h-4 w-4 text-red-300" />
              <span className="text-xs font-semibold text-red-200 uppercase tracking-wider">SBA Backed Program</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.08] mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
              SBA Made in<br /><span className="text-red-300">America</span> Program
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed mb-10 max-w-xl">
              Government backed financing for U.S. manufacturing businesses to support production, scaling, and operational growth with long term capital and improved cash flow.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/apply" data-testid="sba-mia-apply" className="inline-flex items-center gap-2 bg-red-500 text-white font-semibold text-sm px-7 py-3.5 rounded-full hover:bg-red-400 transition-all">
                Check Eligibility <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/contact" className="inline-flex items-center gap-2 border border-white/20 text-white font-semibold text-sm px-7 py-3.5 rounded-full hover:bg-white/10 transition-all">Talk to Us</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Key Highlights Strip */}
      <section className="bg-[#1a1a2e] py-8 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            {[
              { icon: <DollarSign className="h-5 w-5 text-red-400" />, value: 'Up to $1M', label: 'Working Capital' },
              { icon: <TrendingUp className="h-5 w-5 text-red-400" />, value: 'Up to $5M', label: 'Expansion Capital' },
              { icon: <Clock className="h-5 w-5 text-red-400" />, value: '10 Year', label: 'Term Structure' },
              { icon: <BarChart3 className="h-5 w-5 text-red-400" />, value: 'Lower', label: 'Monthly Payments' },
              { icon: <Shield className="h-5 w-5 text-red-400" />, value: 'SBA', label: 'Government Backed' },
            ].map((stat) => (
              <div key={stat.label} className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                  {stat.icon}
                </div>
                <div>
                  <div className="text-sm font-bold text-white" style={{ fontFamily: 'var(--font-heading)' }}>{stat.value}</div>
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who It's For + Use of Funds */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Who It's For */}
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>Who This Program Serves</h2>
              <p className="text-slate-500 mb-10 leading-relaxed">
                The SBA Made in America Program is built for U.S. based manufacturers who contribute to domestic production and economic growth. If your business builds, assembles, fabricates, or produces goods in the United States, this program was structured for you.
              </p>
              <div className="space-y-4">
                {[
                  { icon: <Factory className="h-5 w-5" />, title: 'Manufacturing Operations', desc: 'Producers, fabricators, and assembly operations across all sectors.' },
                  { icon: <Cog className="h-5 w-5" />, title: 'Industrial Production', desc: 'Machine shops, metalwork, plastics, electronics, and component manufacturing.' },
                  { icon: <Hammer className="h-5 w-5" />, title: 'Building and Construction Materials', desc: 'Domestic producers of building products, fixtures, and construction materials.' },
                  { icon: <TrendingUp className="h-5 w-5" />, title: 'Scaling Operations', desc: 'Manufacturers expanding capacity, entering new markets, or modernizing facilities.' },
                ].map((item) => (
                  <div key={item.title} className="flex items-start gap-4 p-5 bg-white border border-slate-200 rounded-xl">
                    <div className="w-10 h-10 rounded-lg bg-red-50 text-red-700 flex items-center justify-center flex-shrink-0">
                      {item.icon}
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-slate-900 mb-0.5">{item.title}</h3>
                      <p className="text-xs text-slate-500">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Use of Funds + Benefits */}
            <div className="space-y-8">
              <div className="relative rounded-2xl overflow-hidden h-52">
                <img src="https://images.pexels.com/photos/1108117/pexels-photo-1108117.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Manufacturing" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a2e]/90 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="text-lg font-bold text-white" style={{ fontFamily: 'var(--font-heading)' }}>Built for American Manufacturing</div>
                  <p className="text-xs text-red-100/80 mt-1">Long term capital solutions designed to replace short term debt and improve cash flow.</p>
                </div>
              </div>

              <div className="bg-white border border-slate-200 rounded-2xl p-8">
                <h3 className="text-xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>Use of Funds</h3>
                {[
                  'Production scaling and capacity expansion',
                  'New equipment, tooling, and technology upgrades',
                  'Facility acquisition, construction, or renovation',
                  'Working capital for materials, inventory, and payroll',
                  'Refinance high cost short term debt',
                  'Supply chain investment and operational improvement',
                ].map((use) => (
                  <div key={use} className="flex items-center gap-3 mb-4 last:mb-0">
                    <CheckCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
                    <span className="text-sm text-slate-600">{use}</span>
                  </div>
                ))}
              </div>

              <Link to="/apply" className="block bg-[#1a1a2e] text-white rounded-2xl p-8 group hover:bg-[#1e2040] transition-colors">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-bold mb-1" style={{ fontFamily: 'var(--font-heading)' }}>Check Your Eligibility</h3>
                    <p className="text-sm text-slate-400">Submit your request to receive a structured approval</p>
                  </div>
                  <ArrowRight className="h-6 w-6 text-red-400 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>Program Benefits</h2>
            <p className="text-slate-500 max-w-xl mx-auto">Government backed capital structured for American manufacturers building the future.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: <DollarSign className="h-6 w-6" />, title: 'Cash Flow Relief', desc: 'Lower monthly payments compared to short term financing, freeing up operating capital for reinvestment in production and growth.', color: 'bg-red-50 text-red-700' },
              { icon: <Clock className="h-6 w-6" />, title: 'Long Term Structure', desc: '10 year terms provide predictable payment schedules and the stability to invest in equipment, facilities, and workforce without constant refinancing.', color: 'bg-blue-50 text-blue-700' },
              { icon: <TrendingUp className="h-6 w-6" />, title: 'Scalable Capital', desc: 'Access up to $5M for expansion and fixed assets, or up to $1M in working capital to support production at scale.', color: 'bg-emerald-50 text-emerald-700' },
            ].map((card) => (
              <div key={card.title} className="bg-slate-50 border border-slate-200 rounded-2xl p-8">
                <div className={`w-12 h-12 rounded-xl ${card.color} flex items-center justify-center mb-6`}>
                  {card.icon}
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>{card.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Qualifier + CTA */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.pexels.com/photos/3846517/pexels-photo-3846517.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#1a1a2e]/90" />
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-8 text-center relative z-10">
          <div className="inline-flex items-center gap-2 bg-red-500/15 border border-red-400/20 rounded-full px-4 py-1.5 mb-6">
            <Shield className="h-4 w-4 text-red-300" />
            <span className="text-xs font-semibold text-red-200 uppercase tracking-wider">SBA Government Backed</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'var(--font-heading)' }}>Ready to Grow American Manufacturing?</h2>
          <p className="text-base text-slate-400 mb-4 max-w-xl mx-auto">Eligibility is determined through a quick review process. Submit your request to receive a structured approval.</p>
          <p className="text-sm text-slate-500 mb-10 max-w-lg mx-auto italic">Long term capital solutions designed to replace short term debt and improve cash flow.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/apply" className="inline-flex items-center gap-2 bg-red-500 text-white font-semibold text-sm px-8 py-4 rounded-full hover:bg-red-400 transition-colors">Check Eligibility <ArrowRight className="h-4 w-4" /></Link>
            <Link to="/contact" className="inline-flex items-center gap-2 border border-white/20 text-white font-semibold text-sm px-8 py-4 rounded-full hover:bg-white/10 transition-colors">Contact Us</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SBAMadeInAmerica;
